import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchInvitationsAPI } from "@/services/api/invitation";
import SearchBox from "@/components/SearchBox/SearchBox";
import styles from "./InvitationDropdown.module.scss";

interface Invitation {
  id: number;
  email: string;
  role: string;
  isActive: boolean;
  userId:number;
}

interface Props {
  value: number; // selected invitation id (0 = none)
  onChange: (id: number, email: string) => void;
  initialLabel?: string; // pre-populated label for edit mode (from replyToUserDto.email)
}

const LIMIT = 10;
const DEBOUNCE_MS = 350;

const InvitationDropdown: React.FC<Props> = ({ value, onChange, initialLabel = "" }) => {
  const [open, setOpen]                   = useState(false);
  const [items, setItems]                 = useState<Invitation[]>([]);
  const [page, setPage]                   = useState(1);
  const [hasMore, setHasMore]             = useState(true);
  const [loading, setLoading]             = useState(false);
  const [search, setSearch]               = useState("");
  // Seed the label immediately from initialLabel (edit mode) — will be overridden once list loads
  const [selectedLabel, setSelectedLabel] = useState<string>(initialLabel);

  const listRef       = useRef<HTMLDivElement>(null);
  const containerRef  = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Close on outside click ────────────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Reset search when dropdown closes ────────────────────────────
  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  // ── Core fetch ────────────────────────────────────────────────────
  const fetchPage = useCallback(async (pageNo: number, term: string, reset = false) => {
    setLoading(true);
    try {
      const res = await fetchInvitationsAPI({ page: pageNo, limit: LIMIT, search: term || null });
      const data = res?.data || res;
      const raw: any[] = data?.results || [];

      // Only keep accepted managers and owners
      const filtered = raw.filter((r) => {
        const role = (r.role || "").toLowerCase();
        const accepted = !!r.name;
        return (role === "manager" || role === "owner") && accepted;
      });

      const results: Invitation[] = filtered.map((r) => ({
        id: r.id,
        userId: r.userId || 0,
        email: r.email || "",
        role: r.role || "",
        isActive: r.isActive || false,
      }));

      setItems(prev => (reset ? results : [...prev, ...results]));
      setHasMore(raw.length === LIMIT);
      setPage(pageNo);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Load page 1 when dropdown first opens ────────────────────────
  useEffect(() => {
    if (open) {
      fetchPage(1, search, true);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Debounced search ─────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setItems([]);
      setPage(1);
      setHasMore(true);
      fetchPage(1, search, true);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Resolve label from loaded items (keeps initialLabel until list loads) ──
  useEffect(() => {
    if (value && items.length > 0) {
      const found = items.find(i => i.userId === value);
      if (found) setSelectedLabel(found.email); // override with real data once available
    } else if (!value) {
      setSelectedLabel("");
    }
    // If value is set but item not found yet, keep existing label (initialLabel)
  }, [value, items]);

  // ── Sync initialLabel when it arrives (async edit load) ──────────
  useEffect(() => {
    if (initialLabel && !selectedLabel) {
      setSelectedLabel(initialLabel);
    }
  }, [initialLabel]);

  // ── Infinite scroll ───────────────────────────────────────────────
  const handleScroll = () => {
    const el = listRef.current;
    if (!el || loading || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      fetchPage(page + 1, search);
    }
  };

  // ── Select / clear ────────────────────────────────────────────────
  const handleSelect = (item: Invitation) => {
    setSelectedLabel(item.email);
    onChange(item.userId, item.email);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedLabel("");
    onChange(0, "");
  };

  return (
    <div className={styles.wrapper} ref={containerRef}>
      {/* ── Trigger ─────────────────────────────── */}
      <button
        type="button"
        className={`${styles.trigger} ${open ? styles.triggerOpen : ""}`}
        onClick={() => setOpen(v => !v)}
      >
        <span className={selectedLabel ? styles.selectedText : styles.placeholder}>
          {selectedLabel || "Select reply-to address..."}
        </span>
        <span className={styles.triggerRight}>
          {selectedLabel && (
            <span className={styles.clearBtn} onClick={handleClear} title="Clear">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </span>
          )}
          <svg
            className={`${styles.chevron} ${open ? styles.chevronUp : ""}`}
            width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      {/* ── Dropdown panel ──────────────────────── */}
      {open && (
        <div className={styles.dropdown}>

          {/* SearchBox */}
          <div className={styles.searchWrap} onClick={e => e.stopPropagation()}>
            <SearchBox
              value={search}
              onChange={val => setSearch(val)}
              // placeholder="Search by email..."
              width="100%"
            />
          </div>

          <div className={styles.divider} />

          {/* "None" option — only when not searching */}
          {/* {!search && (
            <>
              <div
                className={`${styles.option} ${value === 0 ? styles.optionSelected : ""}`}
                onClick={() => { onChange(0, ""); setSelectedLabel(""); setOpen(false); }}
              >
                <span className={styles.optionEmail}>— None (use assessment manager)</span>
              </div>
              <div className={styles.divider} />
            </>
          )} */}

          {/* Scrollable list */}
          <div className={styles.list} ref={listRef} onScroll={handleScroll}>
            {items.length === 0 && !loading && (
              <div className={styles.empty}>
                {search ? `No results for "${search}"` : "No invitations found"}
              </div>
            )}

            {items.map(item => (
              <div
                key={item.id}
                className={`${styles.option} ${value === item.userId ? styles.optionSelected : ""}`}
                onClick={() => handleSelect(item)}
              >
                <span className={styles.optionEmail}>{item.email}</span>
                <span className={styles.optionRole}>{item.role}</span>
              </div>
            ))}

            {loading && (
              <div className={styles.loader}>
                <span className={styles.spinner} />
                {search ? "Searching..." : "Loading..."}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvitationDropdown;
